/* eslint-disable max-len */
import { Dispatch, SetStateAction, useState } from "react";
import { useGetList, useNotify, useTranslate } from "react-admin";
import {
  Avatar,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select
} from "@mui/material";
import * as User from "providers/user";
import { EditButtonIcon } from "assets/Icons";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { muiColors } from "themes/colors";
import * as Axios from "providers/axios";
import { IShowAppointmentServiceDialogData } from "./AppointmentServiceList";
import dayjs, { Dayjs } from "dayjs";
import { DesktopTimePicker, TimePicker } from "@mui/x-date-pickers";

interface AppointmentServiceShowProps {
  showDialogData: IShowAppointmentServiceDialogData;
  setShowDialogData: Dispatch<
    SetStateAction<IShowAppointmentServiceDialogData | null>
  >;
  refresh: () => void;
}

const AppointmentServiceShow = ({
  showDialogData,
  setShowDialogData,
  refresh
}: AppointmentServiceShowProps) => {
  const {
    id,
    client,
    time,
    artistId,
    artist,
    serviceName,
    date,
    artistPhoto,
    clientPhoto,
    guestName,
    appointmentNotes,
    serviceDuration,
    servicePrice,
    locationName,
    addOns,
    startDateAndTime,
    appointmentId,
    locationTimezone,
    isBlockedTime,
    clientPhoneNumber
  } = showDialogData;
  const { api } = Axios.useAxios();
  const notify = useNotify();
  const translate = useTranslate();
  const { permissions } = User.useUser();
  const [selectedArtist, setSelectedArtist] = useState<any>({
    id: artistId,
    name: artist || ""
  });
  const [isArtistEditMode, setIsArtistEditMode] = useState<boolean>(false);
  const [isDateEditMode, setIsDateEditMode] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editedStartTime, setEditedStartTime] = useState<Dayjs | null>(
    startDateAndTime ? dayjs(startDateAndTime) : null
  );

  const { data: artistData, isLoading: isArtistsLoading } = useGetList(
    "Artist",
    {
      filter: {
        user: {
          is: {
            joined_company_id: permissions?.companyId
          }
        },
        is_active: {
          equals: true
        }
      }
    }
  );

  const artistChoices = artistData
    ?.map((item: any) => {
      return {
        id: item?.id,
        name: `${item?.user?.first_name} ${item?.user?.last_name}`
      };
    })
    ?.filter((curr: any) => curr.id !== selectedArtist?.id);

  const handleMoveOneAppointmentServiceTime = async () => {
    if (!editedStartTime || !serviceDuration) return;
    setIsEditing(true);
    const endTime = dayjs(
      dayjs(editedStartTime).add(serviceDuration, "minute")
    );
    try {
      await api.post("/v1/appointments/move-one-appointment-service", {
        appointmentId,
        appointmentServiceId: id,
        startTime: editedStartTime.toISOString(),
        endTime: endTime.toISOString()
      });
      setShowDialogData((prev) => {
        return {
          ...prev,
          startDateAndTime: editedStartTime,
          date: editedStartTime.tz(locationTimezone).format("D MMMM YYYY"),
          time: `${editedStartTime
            .tz(locationTimezone)
            .format("hh:mm A")} - ${endTime
            .tz(locationTimezone)
            .format("hh:mm A")}`
        };
      });
      setTimeout(() => {
        refresh();
        setIsEditing(false);
        setIsDateEditMode(false);
      }, 500);
    } catch (e) {
      notify(
        "Error occurred during editing date and time, please try again later",
        {
          type: "error",
          undoable: false,
          anchorOrigin: { vertical: "bottom", horizontal: "center" }
        }
      );
      setIsEditing(false);
    }
  };

  const handleEditArtist = async () => {
    setIsEditing(true);
    try {
      await api.post("/v1/appointments/appointment-service-edit", {
        appointmentServiceId: id,
        artistId: selectedArtist?.id
      });
      setShowDialogData((prev) => {
        const findArtist = artistData?.find((i) => i.id === selectedArtist?.id);
        return {
          ...prev,
          artistId: selectedArtist?.id,
          artist: selectedArtist?.name,
          artistPhoto:
            findArtist?.cover_photo_url ||
            findArtist?.user?.photo_url ||
            "monogram"
        };
      });
      setTimeout(() => {
        refresh();
        setIsEditing(false);
        setIsArtistEditMode(false);
      }, 500);
    } catch (e) {
      notify(translate("common.something_went_wrong"), {
        type: "error",
        undoable: false,
        anchorOrigin: { vertical: "bottom", horizontal: "center" }
      });
      setIsEditing(false);
    }
  };

  return (
    <div className="w-full px-[16px] py-[8px]">
      <div className="mb-[8px]">
        <div className="text-[12px] font-[400] font-['Inter'] text-mono-secondaryTextColor">
          {translate("appointment.date")}:
        </div>
        <div className="text-[16px] font-[400] font-['Inter'] text-mono-primaryTextColor">
          {date}
        </div>
      </div>
      <div className="mb-[8px]">
        {!isDateEditMode && (
          <div className="text-[12px] font-[400] font-['Inter'] text-mono-secondaryTextColor">
            {translate("appointment.time")}:
          </div>
        )}
        {isDateEditMode ? (
          <div className={`flex items-center gap-[8px] mt-[2px]`}>
            <Box
              sx={{
                "& .MuiFilledInput-root": {
                  borderRadius: "8px",
                  border: "1px solid rgba(48, 47, 46, 0.06)",
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.06)"
                  },
                  "& fieldset": {
                    border: "solid 1px transparent"
                  },
                  "&:hover fieldset": {
                    border: "solid 1px transparent"
                  },
                  "&.Mui-focused fieldset": {
                    border: "solid 1px #b8722a"
                  }
                },
                "& .MuiFilledInput-input": {
                  height: "18px",
                  fontSize: "16px",
                  borderRadius: "8px",
                  fontFamily: "Inter",
                  border: "none",
                  paddingTop: "22px",
                  paddingBottom: "10px"
                }
              }}
            >
              <div className="relative w-full">
                <DesktopTimePicker
                  label={translate("appointment.time")}
                  value={editedStartTime}
                  onChange={(newValue) => setEditedStartTime(newValue)}
                  timezone={locationTimezone}
                  sx={{
                    "& .MuiIconButton-root": {
                      "& svg": {
                        fill: "#8c8c8c"
                      }
                    }
                  }}
                />
                <TimePicker
                  label={translate("appointment.time")}
                  value={editedStartTime}
                  onChange={(newValue) => setEditedStartTime(newValue)}
                  timezone={locationTimezone}
                  sx={{
                    position: "absolute",
                    top: "0px",
                    right: "0px",
                    width: "44px !important",
                    height: "52px !important",
                    "& .MuiInputLabel-root": { display: "none" },
                    "& .MuiFilledInput-root": {
                      border: "none !important",
                      paddingRight: "24px !important",
                      backgroundColor: "transparent !important"
                    },
                    "& .MuiFilledInput-root:before": {
                      border: "none !important"
                    },
                    "& .MuiFilledInput-root:after": {
                      border: "none !important"
                    },
                    "& .MuiFilledInput-root.Mui-error:after": {
                      border: "none !important"
                    },
                    "& .MuiFilledInput-root.Mui-focused": {
                      backgroundColor: "transparent"
                    },
                    "& .MuiFilledInput-input": {
                      height: "20px",
                      marginRight: "-8px",
                      paddingLeft: "0px !important",
                      visibility: "hidden"
                    },
                    "& .MuiIconButton-root": {
                      marginRight: "0px !important",
                      "& svg": {
                        fill: "#8c8c8c"
                      }
                    }
                  }}
                />
              </div>
            </Box>
            <IconButton
              onClick={() => {
                setEditedStartTime(
                  startDateAndTime ? dayjs(startDateAndTime) : null
                );
                setIsDateEditMode(false);
              }}
            >
              <CloseIcon />
            </IconButton>
            <IconButton
              onClick={handleMoveOneAppointmentServiceTime}
              disabled={
                dayjs(editedStartTime).isSame(dayjs(startDateAndTime)) ||
                !editedStartTime ||
                !serviceDuration ||
                isEditing
              }
              sx={{
                color: muiColors.primary,
                ".Mui-disabled": { color: "red" }
              }}
            >
              {isEditing ? (
                <CircularProgress style={{ width: 20, height: 20 }} />
              ) : (
                <CheckIcon />
              )}
            </IconButton>
          </div>
        ) : (
          <div className={`flex items-center gap-[8px] mt-[2px]`}>
            <div className="text-[16px] font-[400] font-['Inter'] text-mono-primaryTextColor">
              {time}
            </div>
            {!isBlockedTime && !permissions.isArtist && (
              <IconButton
                onClick={() => {
                  setIsDateEditMode(true);
                  setIsArtistEditMode(false);
                }}
              >
                <EditButtonIcon />
              </IconButton>
            )}
          </div>
        )}
      </div>
      <div className="mb-[8px]">
        <div className="text-[12px] font-[400] font-['Inter'] text-mono-secondaryTextColor">
          {translate("appointment.location")}:
        </div>
        <div className="text-[16px] font-[400] font-['Inter'] text-mono-primaryTextColor">
          {locationName}
        </div>
      </div>
      {!isBlockedTime && (
        <div className="mb-[8px]">
          <div className="text-[12px] font-[400] font-['Inter'] text-mono-secondaryTextColor">
            {!!addOns?.length
              ? translate("appointment.service_and_add_ons")
              : translate("appointment.service")}
            :
          </div>
          <div className="text-[16px] font-[400] font-['Inter'] text-mono-primaryTextColor">
            {serviceName}
            {!!addOns?.length
              ? ` (${addOns
                  ?.map((item: any) => item.core_service.name)
                  ?.join(", ")})`
              : ""}
          </div>
        </div>
      )}
      <div className="mb-[8px]">
        <div className="text-[12px] font-[400] font-['Inter'] text-mono-secondaryTextColor">
          {translate("services.duration")}:
        </div>
        <div className="text-[16px] font-[400] font-['Inter'] text-mono-primaryTextColor">
          {serviceDuration} {"minutes"}
        </div>
      </div>
      {!isBlockedTime && (
        <div className="mb-[8px]">
          <div className="text-[12px] font-[400] font-['Inter'] text-mono-secondaryTextColor">
            {translate("services.price")}:
          </div>
          <div className="text-[16px] font-[400] font-['Inter'] text-mono-primaryTextColor">
            {"$"}
            {servicePrice}
          </div>
        </div>
      )}
      <div className="mb-[8px]">
        {!isArtistEditMode && (
          <div className="text-[12px] font-[400] font-['Inter'] text-mono-secondaryTextColor">
            {translate("appointmentservices.artist")}:
          </div>
        )}
        {isArtistEditMode ? (
          <div className={`flex items-center gap-[8px] mt-[2px]`}>
            <FormControl
              fullWidth
              size="small"
              // sx={{
              //   backgroundColor: editedArtists?.find((i) => i?.id === item?.id)
              //     ? "#e3ffdd"
              //     : null
              // }}
            >
              <InputLabel id="artists-custom-select">
                {translate("order.artist")}
              </InputLabel>
              <Select
                labelId="artists-custom-select"
                id="artists-custom-select"
                variant="filled"
                value={selectedArtist}
                onChange={(event) => {
                  setSelectedArtist(event?.target?.value);
                }}
                renderValue={(value) => {
                  return <span>{value?.name}</span>;
                }}
              >
                {isArtistsLoading && artistChoices?.length === 0 ? (
                  <div className="w-full h-[60px] flex justify-center items-center">
                    <CircularProgress />
                  </div>
                ) : (
                  artistChoices?.map((item: any, index: number) => (
                    <MenuItem key={index} value={item}>
                      {item?.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <IconButton
              onClick={() => {
                setSelectedArtist({
                  id: artistId,
                  name: artist || ""
                });
                setIsArtistEditMode(false);
              }}
            >
              <CloseIcon />
            </IconButton>
            <IconButton
              onClick={() => {
                handleEditArtist();
              }}
              disabled={artistId === selectedArtist?.id || isEditing}
              sx={{
                color: muiColors.primary,
                ".Mui-disabled": { color: "red" }
              }}
            >
              {isEditing ? (
                <CircularProgress style={{ width: 20, height: 20 }} />
              ) : (
                <CheckIcon />
              )}
            </IconButton>
          </div>
        ) : (
          <div className={`flex items-center gap-[8px] mt-[2px]`}>
            <Avatar
              sx={{
                bgcolor: "rgba(238, 234, 228, 0.9)",
                color: "rgba(48, 47, 46, 0.95)",
                width: "32px",
                height: "32px",
                fontFamily: "Inter",
                fontSize: "18px"
              }}
              alt={artist}
              src={artistPhoto}
            />
            <div className="line-clamp-1 break-all text-[16px] font-[400] font-['Inter'] text-mono-primaryTextColor">
              {artist}
            </div>
            {!isBlockedTime && !permissions.isArtist && (
              <IconButton
                onClick={() => {
                  setIsArtistEditMode(true);
                  setSelectedArtist({
                    id: artistId,
                    name: artist || ""
                  });
                  setIsDateEditMode(false);
                }}
              >
                <EditButtonIcon />
              </IconButton>
            )}
          </div>
        )}
      </div>
      {!isBlockedTime && (
        <div className="mb-[8px]">
          <div className="text-[12px] font-[400] font-['Inter'] text-mono-secondaryTextColor">
            {translate("appointment.client")}:
          </div>
          {!!guestName ? (
            <div className="line-clamp-1 break-all text-[16px] font-[400] font-['Inter'] text-mono-primaryTextColor">
              {guestName}
            </div>
          ) : (
            <div className={`flex just items-center gap-[8px] mt-[2px]`}>
              <Avatar
                sx={{
                  bgcolor: "rgba(238, 234, 228, 0.9)",
                  color: "rgba(48, 47, 46, 0.95)",
                  width: "32px",
                  height: "32px",
                  fontFamily: "Inter",
                  fontSize: "18px"
                }}
                alt={client}
                src={clientPhoto}
              />
              <div className="line-clamp-1 break-all text-[16px] font-[400] font-['Inter'] text-mono-primaryTextColor">
                {client}
              </div>
            </div>
          )}
        </div>
      )}
      {!isBlockedTime && !permissions.isArtist && (
        <div className="mb-[8px]">
          <div className="text-[12px] font-[400] font-['Inter'] text-mono-secondaryTextColor">
            {translate("appointment.client_phone_number")}:
          </div>
          <div className="text-[16px] font-[400] font-['Inter'] text-mono-primaryTextColor">
            {clientPhoneNumber || "-"}
          </div>
        </div>
      )}
      {!!appointmentNotes && (
        <div className="mb-[8px]">
          <div className="text-[12px] font-[400] font-['Inter'] text-mono-secondaryTextColor">
            Appointment note:
          </div>
          <div className="text-[16px] font-[400] font-['Inter'] text-mono-primaryTextColor">
            {appointmentNotes}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentServiceShow;
